import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useState,
  useRef,
  ReactNode,
  createContext
} from 'react';

import DocViewer, {
  DocViewerRenderers,
  IHeaderOverride
} from '@cyntler/react-doc-viewer';
import UTIF from 'utif';
import {Buffer} from 'buffer';
import {saveAs} from 'file-saver';
import JSZip from 'jszip';
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef
} from 'react-zoom-pan-pinch';

import {
  createAuditLog,
  downloadAsPdf,
  getCmsData,
  getFileData,
  getFileVersionData,
  getFileVersions,
  getLMSFileData
} from '@shared/services/service-handlers/PrivateService';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import {
  DocumentScanner,
  Refresh,
  ZoomIn,
  ZoomOut,
  Title,
  West,
  East
} from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {useTranslation} from 'react-i18next';
import FormConstants from '@shared/constants/FormConstants';
import AuthUtils from 'src/utils/AuthUtils';
import axios, {CancelTokenSource} from 'axios';
import React from 'react';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackbarConfig from '@components/snackbar/SnackbarConfig';
import {useSnackbar} from 'notistack';
import SelectInput from '@components/form/select-input/SelectInput';
import {useParams} from 'react-router';
import {LoadingButton} from '@mui/lab';

import {useAppContext} from '@context/AppContext';
import {useModuleContext} from '@context/ModuleContext';
import ReactPDFRenderer from '@components/docViewer/CustomRenderers/PDFRenderer';
import RTFRenderer from '@components/docViewer/CustomRenderers/RTFRenderer';
import TiffRenderer from '@components/docViewer/CustomRenderers/TiffRenderer';
import TxtRenderer from '@components/docViewer/CustomRenderers/TxtRenderer';
import {IconButtonWrapper} from '@components/docViewer/MultiDocumentViewer/MultiDocumentViewerStyles';
// import {height} from '@mui/system';
import {id} from 'date-fns/locale';
import {t} from 'i18next';
import {node} from 'prop-types';
import theme from 'src/theme';
import EncryptUtils from 'src/utils/EncryptUtils';
import {
  TextContainer,
  PreviewerContainer,
  MainContainerDiv,
  ZoomButtonsDiv,
  ZoomButton,
  PanToMoveDiv,
  AdditionalFeaturesButtonDiv,
  DocViewContainer
} from './CustomLMSViewerStyles';

interface Document {
  ID: number | string;
  LegacyApp: string;
  LegacyMRN: string;
  SourceDocumentID: string;
  FileExtensions: string[];
  IsNotes: boolean[];
  Name: string;
  IsMultiPage: string;
  Sequence: number[];
  DocumentTypeID: number;
  ChildNodes?: any[];
  HasDocuments?: boolean;
}

interface ActionViewObj {
  actionLabel: string;
  actionFunction: string;
}

interface ActionDownloadObj {
  actionLabel: string;
  actionFunction: string;
  permissions: any;
}

interface CustomDocViewerActions {
  viewActions: ActionViewObj;
  downloadActions: ActionDownloadObj;
}

interface CustomDocViewerProps {
  docData: any;
  setDocData: Dispatch<SetStateAction<any>>;
  variables?: any;
  UIFieldData: any;
  filterSchemaClause?: string;
  docDownloadData?: any;
  cardValues?: any;
  // eslint-disable-next-line react/boolean-prop-naming
  height?: any;
  top?: string;
}
// required for zoomIn, zoomOut, and reset functions
interface ControlsProps {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
}

export const DocViewerContext = createContext({textSelectMode: false});

const CustomLMSDocViewer = (props: CustomDocViewerProps): ReactElement => {
  const {UIFieldData, docData, setDocData, height} = props;

  const unSupportedFormats = FormConstants.DOCUMENT_FORMATS.unSupportedFormats;
  const SupportedFormats = FormConstants.DOCUMENT_FORMATS.SupportedFormats;
  const iframeSupportedFormats =
    FormConstants.DOCUMENT_FORMATS.iframeSupportedFormats;
  const [iframeSrc, setIframeSrc] = useState<any>('');
  const {
    appState: {applicationName}
  } = useAppContext();
  const {
    moduleState: {isBH, isBHReason}
  } = useModuleContext();

  const theme = useTheme();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const source = React.useRef<CancelTokenSource | null>(null);
  // eslint-disable-next-line prefer-const
  const {Id: id} = docData;
  const [fileExtensions, setFileExtensions] = useState(
    docData?.FileExtensions ?? []
  );
  const params = useParams();
  const [docs, setDocs] = useState<any>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [previewer, setPreviewer] = useState<ReactNode | null>(null);
  const [activeDocument, setActiveDocument] = useState({uri: ''});
  const [bufferData, setBufferData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [docVersionsData, setDocVersionsData] = useState([]);
  const [selectedDocVersion, setSelectedDocVersion] = useState('');
  const [selectedDocVersionIds, setSelectedDocVersionIds] = useState<any>([]);
  const [textSelectMode, setTextSelectMode] = useState(false);
  const [isMultiPage, setIsMultiPage] = useState(false);

  const ZoomInShowTextRef = useRef(0);
  // required for zoomIn, zoomOut, and reset functions
  const transformComponentRef = useRef<ReactZoomPanPinchRef>(null);

  const zoomIn = () => {
    transformComponentRef.current?.zoomIn();
  };

  const zoomOut = () => {
    transformComponentRef.current?.zoomOut();
  };

  const resetTransform = () => {
    transformComponentRef.current?.resetTransform();
  };

  useEffect(() => {
    if (!isLoading) {
      ZoomInShowTextRef.current = 0;
    }
  }, [ZoomInShowTextRef.current]);

  const getBase64Flag = (extension: string) => {
    if (extension) {
      switch (extension?.toLowerCase()) {
        case 'jpeg':
          return 'data:image/jpeg;base64,';
        case 'jpg':
          return 'data:image/jpg;base64,';
        case 'gif':
          return 'data:image/gif;base64,';
        case 'png':
          return 'data:image/png;base64,';
        case 'bmp':
          return 'data:image/bmp;base64,';
        case 'tiff':
          return 'data:image/tiff;base64,';
        case 'tif':
          return 'data:image/tif;base64,';
        case 'pdf':
          return 'data:application/pdf;base64,';
        case 'txt':
          return 'data:text/txt;base64,';
        case 'rtf':
          return 'data:image/rtf;base64,';
        case 'html':
          return 'data:text/html;base64,';
        case 'htm':
          return 'data:text/html;base64,';
        case 'doc':
          return 'data:application/msword;base64,';
        case 'docx':
          return 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,';
        default:
          return 'data:text/txt;base64,';
      }
    } else {
      return 'data:text/txt;base64,';
    }
  };

  //   const downloadRTF = (bufferURI: any, fileName: any) => {
  //     const bufferString = bufferURI.replace(
  //       /^(data:image\/tiff;base64|data:image\/tif;base64|data:image\/rtf;base64,)/i,
  //       ''
  //     );
  //     const nodeJSBuffer = Buffer.from(bufferString, 'base64');
  //     // Convert the Buffer to a Blob
  //     const buffer = Buffer.from(nodeJSBuffer);
  //     const blob = new Blob([buffer], {type: 'application/rtf'});

  //     // Create a download link
  //     const downloadLink = document.createElement('a');
  //     const blobURL = URL.createObjectURL(blob);
  //     downloadLink.href = blobURL;
  //     downloadLink.download = fileName || 'output.rtf';

  //     // Append the link to the document and trigger a click event
  //     document.body.appendChild(downloadLink);
  //     downloadLink.click();

  //     // Clean up by removing the link and revoking the blob URL
  //     document.body.removeChild(downloadLink);
  //     URL.revokeObjectURL(blobURL);
  //   };

  const getBase64Data = (responseBufferData: any[], file_extensions: any[]) => {
    const base64DocsData: any = [];
    let tempText = '';
    responseBufferData.forEach((eachBuffer: any, index: number) => {
      if (eachBuffer) {
        base64DocsData.push({
          uri: `${getBase64Flag(file_extensions[index])}${Buffer.from(
            eachBuffer.data
          )?.toString('base64')}${tempText}`,
          index: index
        });
        tempText = tempText + ' ';
      }
    });
    setDocs(base64DocsData);
    setActiveDocument(base64DocsData[0]);
    // setDocDownloadData(base64DocsData);
  };

  const getVersionsData = async (data: any) => {
    try {
      const encryptedData = EncryptUtils.encryptedData(data);
      const responseVersions = await getFileVersions({tk: encryptedData});
      setDocVersionsData(responseVersions?.versions_data);
      const tempObj = {...responseVersions?.versions_data?.[0]};
      setDocData((prevState: any) => {
        return {...prevState, ...tempObj};
      });
      setSelectedDocVersion(responseVersions?.versions_data[0]?.Version);
    } catch (err) {
      setIsLoading(false);
      if (err !== 'canceled') {
        const errMessage = getErrorMessage(err);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    }
  };

  //   const logDocumentDownload = (dataObj: any) => {
  //     const version = selectedDocVersion;
  //     const tempObj: any = docVersionsData?.filter(
  //       (item: any) => item?.Version == version
  //     )[0];

  //     const versionID: any = selectedDocVersionIds?.length
  //       ? selectedDocVersionIds
  //       : tempObj.VersionID;
  //     const updatedDocData: any = {...docData, ...tempObj};

  //     const docDataObj: any = {
  //       fileName: cardValues?.[0]?.name,
  //       SourceDocumentID: updatedDocData
  //         ? updatedDocData?.SourceDocumentID
  //         : docData?.SourceDocumentID ?? '',
  //       ids: versionID,
  //       version: version ? version : '1',
  //       downloadType: dataObj.downloadType,
  //       procedure_type,
  //       accession_number
  //     };

  //     const keyValueString = Object.entries(docDataObj)
  //       .map(([key, value]) => {
  //         if (value) return `${key}: ${JSON.stringify(value)}`;
  //         return null;
  //       })
  //       .filter(Boolean)
  //       .join(', ');
  //     const data = {
  //       module: params.module_name,
  //       sub_module: `${params.submodule_name ? params.submodule_name + ' -> ' : ''} ${FormConstants.COMMON.DOWNLOADED_DOCUMENT}`,
  //       sub_module_identifier: versionID,
  //       procedure_type,
  //       application_name: applicationName,
  //       isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
  //       isBH_reason: isBHReason,
  //       event_description: keyValueString,
  //       audit_identifier: AuthUtils.getAuditIdentifier(),
  //       audit_identifier_type: AuthUtils.getAuditIdentifierType(),
  //       audit_legacy_app: AuthUtils.getAuditLegacyApp()
  //     };
  //     createAuditLog(params.module_name ?? '', data);
  //   };

  const getDocumentsData = async (
    version?: string,
    versionID?: any,
    updatedDocData?: any
  ) => {
    setIsLoading(true);
    setBufferData([]);
    setActiveDocument({uri: ''});
    // setDocDownloadData([]);
    setFileExtensions([]);
    setIframeSrc('');

    const auditObj = {
      is_audit: true,
      audit_event_description: FormConstants.COMMON.VIEWED_DOCUMENT
    };
    try {
      if (source.current) {
        source.current.cancel();
      }
      source.current = axios.CancelToken.source();
      // const data: any = {
      //   sql_data: UIFieldData
      // };
      // const encryptRequestData = EncryptUtils.encryptedData(data);
      // const Uploadeddata = await getCmsData(
      //   'LMS',
      //   'content-management',
      //   {
      //     tk: encryptRequestData
      //   },
      //   docData.Id,
      //   'fetching'
      // );
      if (!['linkcode', 'embeddedcode'].includes(docData.MimeType)) {
        const response = await getLMSFileData(docData, source.current);
        // eslint-disable-next-line camelcase
        const {buffer_data: responseBufferData, file_extensions} = response;
        // eslint-disable-next-line camelcase
        if (file_extensions && responseBufferData) {
          const extractedType = file_extensions.map((data: string) =>
            data.includes('/') ? data.split('/')[1] : data
          );
          setFileExtensions(extractedType);
          setBufferData(responseBufferData);
          getBase64Data(responseBufferData, extractedType);
        }
      } else {
        setIframeSrc(docData);
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      if (err !== 'canceled') {
        const errMessage = getErrorMessage(err);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    }
  };

  useEffect(() => {
    AuthUtils.setSelectedDocID(id?.toString());
    getDocumentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setTextSelectMode(false);
  }, [id]);

  const isMultiPageTIFF = (base64Data: string): boolean => {
    // Convert base64 to ArrayBuffer
    const byteString = atob(base64Data.split(',')[1]);
    const buffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(buffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }

    // Parse the TIFF file
    const ifds = UTIF.decode(buffer);
    return ifds.length > 1; // Multi-page if more than one IFD (Image File Directory)
  };

  //   useEffect(() => {
  //     setIsMultiPage(false);
  //     if (['tif', 'tiff'].includes(fileExtensions?.[0]?.toLowerCase())) {
  //       const base64Data = docDownloadData[0]?.uri;
  //       if (base64Data) {
  //         const multiPage = isMultiPageTIFF(base64Data);
  //         setIsMultiPage(multiPage);
  //       }
  //     }
  //   }, [docDownloadData, fileExtensions]);

  //   const downloadFile = async (
  //     downloadData: any[],
  //     fileExtensions: string[],
  //     fileName: string
  //   ) => {
  //     setIsDownloading(true);
  //     logDocumentDownload({downloadType: 'Downloaded Original'});

  //     if (downloadData.length > 0) {
  //       if (downloadData?.length === 1) {
  //         if (
  //           ['rtf', 'tiff', 'tif'].includes(fileExtensions?.[0]?.toLowerCase())
  //         ) {
  //           downloadRTF(
  //             downloadData?.[0]?.uri,
  //             `${fileName}.${fileExtensions?.[0]?.toLowerCase()}`
  //           );
  //         } else {
  //           const a = document.createElement('a');
  //           a.setAttribute('href', `${downloadData?.[0]?.uri}`);
  //           a.setAttribute(
  //             'download',
  //             `${fileName}.${fileExtensions[0]?.toLowerCase()}`
  //           );
  //           a.click();
  //         }
  //       } else {
  //         const zip = new JSZip();
  //         downloadData.map((item: any, index: any) => {
  //           // eslint-disable-next-line no-unsafe-optional-chaining
  //           const [fileFlag, fileData] = item?.uri?.split(',');
  //           const fileExtension = fileFlag
  //             ?.split('/')?.[1]
  //             ?.split(';')?.[0]
  //             ?.toLowerCase();

  //           const invalidCharacters = /[\\/:*?"<>|]/g;
  //           const sanitizedFileName = fileName.replace(invalidCharacters, '_');
  //           zip.file(`${sanitizedFileName}_${index}.${fileExtension}`, fileData, {
  //             base64: true
  //           });
  //         });
  //         zip.generateAsync({type: 'blob'}).then(function (content) {
  //           saveAs(content, `${fileName}.zip`);
  //         });
  //       }
  //     }
  //     setIsDownloading(false);
  //   };

  //   const downloadAllPages = async (
  //     isMultiPage: boolean,
  //     downloadData: any[],
  //     fileExtensions: string[],
  //     fileName: string
  //   ) => {
  //     if (!isMultiPage) {
  //       // If it's not multi-page, fallback to the standard download
  //       downloadFile(downloadData, fileExtensions, fileName);
  //       return;
  //     }
  //     setIsDownloading(true);
  //     const base64Data = docDownloadData[0]?.uri;

  //     if (base64Data) {
  //       const zip = new JSZip();

  //       // Convert base64 to ArrayBuffer
  //       const byteString = atob(base64Data.split(',')[1]);
  //       const buffer = new ArrayBuffer(byteString.length);
  //       const intArray = new Uint8Array(buffer);
  //       for (let i = 0; i < byteString.length; i++) {
  //         intArray[i] = byteString.charCodeAt(i);
  //       }

  //       // Decode this file and process each page
  //       const ifds = UTIF.decode(buffer);

  //       for (let i = 0; i < ifds.length; i++) {
  //         UTIF.decodeImage(buffer, ifds[i]);
  //         const rgba = UTIF.toRGBA8(ifds[i]);
  //         const width = ifds[i].width;
  //         const height = ifds[i].height;

  //         // Create canvas to render file
  //         const canvas = document.createElement('canvas');
  //         canvas.width = width;
  //         canvas.height = height;
  //         const ctx = canvas.getContext('2d');
  //         if (!ctx) continue;

  //         // Draw this page as ImageData
  //         const imageData = ctx.createImageData(width, height);
  //         imageData.data.set(rgba);
  //         ctx.putImageData(imageData, 0, 0);

  //         // Convert canvas to Original Blob and add to ZIP file
  //         const blob = await new Promise<Blob | null>(resolve =>
  //           canvas.toBlob(resolve, `image/${fileExtensions}`)
  //         );
  //         const invalidCharacters = /[\\/:*?"<>|]/g;
  //         const sanitizedFileName = fileName.replace(invalidCharacters, '_');
  //         if (blob) {
  //           zip.file(`${sanitizedFileName}_${i + 1}.${fileExtensions}`, blob);
  //         }
  //       }

  //       // Generate ZIP file and trigger download
  //       zip.generateAsync({type: 'blob'}).then(function (content) {
  //         saveAs(content, `${fileName}.zip`);
  //       });
  //     }
  //     setIsDownloading(false);
  //   };

  // Sends Document Details, instead of data stream
  //   const downloadFileAsPDF = async (
  //     version?: string,
  //     versionID?: any,
  //     updatedDocData?: any
  //   ) => {
  //     setIsDownloading(true);

  //     const tempObj: any = docVersionsData?.filter(
  //       (item: any) => item?.Version == version
  //     )[0];

  //     versionID = selectedDocVersionIds?.length
  //       ? selectedDocVersionIds
  //       : tempObj.VersionID;
  //     updatedDocData = {...docData, ...tempObj};

  //     const auditObj = {
  //       is_audit: true,
  //       audit_event_description: FormConstants.COMMON.VIEWED_DOCUMENT
  //     };

  //     const [firstElem]: any = id;

  //     const data: any = {
  //       PatientID,
  //       SourceDocumentID: updatedDocData
  //         ? updatedDocData?.SourceDocumentID
  //         : docData?.SourceDocumentID ?? '',
  //       versions: documentVersionSQL,
  //       versionData: documentVersionDataSQL,
  //       sql: documentDataSql,
  //       variables: variables,
  //       ids: versionID,
  //       filterSchemaClause,
  //       module: params.module_name,
  //       sub_module: params.submodule_name,
  //       audit_sub_module_identifier: firstElem,
  //       audit_identifier: AuthUtils.getAuditIdentifier(),
  //       audit_identifier_type: AuthUtils.getAuditIdentifierType(),
  //       audit_legacy_app: AuthUtils.getAuditLegacyApp(),
  //       version: version ? version : '1',
  //       legacyMRN: recordId,
  //       isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
  //       isBH_reason: isBHReason,
  //       application_name: applicationName,
  //       procedure_type,
  //       accession_number
  //     };

  //     data.sql_data = auditObj;

  //     try {
  //       const response = await downloadAsPdf(data);
  //       logDocumentDownload({downloadType: 'Download as PDF', ...data});
  //       const url = window.URL.createObjectURL(
  //         new Blob([response], {type: 'application/pdf'})
  //       );

  //       // Create a temporary anchor element and trigger the download
  //       const link = document.createElement('a');
  //       link.href = url;
  //       link.setAttribute('download', `${cardValues?.[0]?.name}.pdf`); // The file name
  //       document.body.appendChild(link);
  //       link.click();

  //       // Clean up
  //       document.body.removeChild(link);
  //       window.URL.revokeObjectURL(url);
  //     } catch (err) {
  //       setIsLoading(false);
  //       if (err) {
  //         const errMessage = getErrorMessage(err);
  //         console.error('Error during file download:', errMessage);
  //         enqueueSnackbar(
  //           errMessage?.length ? errMessage : 'Error during file download',
  //           SnackbarConfig.getError()
  //         );
  //       }
  //     } finally {
  //       setIsDownloading(false);
  //     }
  //   };

  const handleDocumentChange = (document: any) => {
    setActiveDocument(document);
  };

  const DocHeaderComponent: IHeaderOverride = (
    state,
    previousDocument,
    nextDocument
  ) => {
    if (
      state.documents.length === 1 ||
      !state.currentDocument ||
      state.config?.header?.disableFileName
    ) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          width: '52vw',
          maxWidth: '52vw',
          paddingY: 1
        }}>
        <Typography
          variant="body1"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginX: '1rem',
            fontWeight: 'bold'
          }}>
          Document {state.currentFileNo + 1} of {state.documents.length}
        </Typography>
        <IconButtonWrapper
          type="button"
          onClick={previousDocument}
          disabled={state.currentFileNo === 0}
          bgcolor={state.currentFileNo === 0 ? 'gray' : 'black'}
          sx={{
            '&:hover': {
              backgroundColor: state.currentFileNo === 0 ? 'gray' : 'black'
            }
          }}>
          <West
            sx={{
              color: 'white'
            }}
          />
        </IconButtonWrapper>
        <IconButtonWrapper
          type="button"
          onClick={nextDocument}
          disabled={state.currentFileNo >= state.documents.length - 1}
          bgcolor={
            state.currentFileNo >= state.documents.length - 1 ? 'gray' : 'black'
          }
          sx={{
            '&:hover': {
              backgroundColor:
                state.currentFileNo >= state.documents.length - 1
                  ? 'gray'
                  : 'black'
            }
          }}>
          <East
            sx={{
              color: 'white'
            }}
          />
        </IconButtonWrapper>
      </Box>
    );
  };

  const renderPreviewer = (): any => {
    let initialPreviewer: any;

    fileExtensions.forEach((eachExtension: string) => {
      if (!SupportedFormats.includes(eachExtension.toUpperCase())) {
        initialPreviewer = (
          <TextContainer
            height={height}
            width={'min-content'}
            maxWidth={'50vw'}>
            <Typography variant="h6">
              {t('PRIVATE.DOCUMENT.NO_PREVIEW_AVAILABLE', {
                ns: 'private'
              })}
            </Typography>
          </TextContainer>
        );
      }
    });

    if (!initialPreviewer) {
      if (
        !iframeSupportedFormats.includes(fileExtensions[0]?.toLowerCase()) &&
        typeof activeDocument?.uri === 'string' &&
        docs?.length
      ) {
        initialPreviewer = (
          <PreviewerContainer>
            <DocViewer
              documents={docs}
              pluginRenderers={[
                ...DocViewerRenderers,
                TiffRenderer,
                RTFRenderer,
                TxtRenderer,
                ReactPDFRenderer
              ]}
              initialActiveDocument={activeDocument}
              activeDocument={activeDocument}
              onDocumentChange={handleDocumentChange}
              config={{
                header: {
                  overrideComponent: DocHeaderComponent
                },
                pdfVerticalScrollByDefault: true
              }}
              style={{
                paddingBottom: '3rem',
                width: '100%',
                height: '100%',
                minWidth: '45vw',
                minHeight: '72vh',
                backgroundColor: '#F4F6F8'
              }}
            />
          </PreviewerContainer>
        );
      }
    }
    return initialPreviewer;
  };

  const getPreviewer = React.useMemo(() => {
    return (
      <Box>
        {textSelectMode ? (
          <MainContainerDiv
            style={{
              display: 'flex',
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'start',
              marginLeft: '10px',
              overflow: 'hidden',
              minHeight: '60vh'
            }}>
            {renderPreviewer()}
          </MainContainerDiv>
        ) : (
          activeDocument?.uri && (
            <TransformWrapper
              ref={transformComponentRef}
              initialScale={1}
              initialPositionX={0}
              initialPositionY={0}
              minScale={0.5}
              maxScale={10}
              limitToBounds={true}
              doubleClick={{disabled: true}}
              wheel={{
                disabled: true,
                wheelDisabled: true,
                touchPadDisabled: true
              }}>
              {({...rest}) => (
                <MainContainerDiv>
                  <TransformComponent
                    contentStyle={{
                      display: 'flex',
                      position: 'relative',
                      alignItems: 'center',
                      justifyContent: 'start',
                      marginLeft: '10px',
                      overflow: 'hidden',
                      minHeight: '60vh'
                      // marginTop: '150px',
                    }}>
                    {renderPreviewer()}
                  </TransformComponent>
                </MainContainerDiv>
              )}
            </TransformWrapper>
          )
        )}
      </Box>
    );
  }, [textSelectMode, activeDocument, docs]);

  //   const viewDocumentAction = React.useCallback((): ReactElement => {
  //     return (
  //       <Stack
  //         sx={{
  //           px: 3
  //         }}>
  //         <LoadingButton
  //           type="submit"
  //           variant={shoulddisplaymetainfo ? 'outlined' : 'contained'}
  //           onClick={() => setShoulddisplaymetainfo(!shoulddisplaymetainfo)}
  //           sx={{
  //             bgcolor: theme.palette.primary.main
  //           }}>
  //           {shoulddisplaymetainfo
  //             ? t('PRIVATE.DOCUMENT.HIDE_DETAILS', {
  //                 ns: 'private'
  //               })
  //             : t('PRIVATE.DOCUMENT.VIEW_DETAILS', {
  //                 ns: 'private'
  //               })}
  //         </LoadingButton>
  //       </Stack>
  //     );
  //   }, [shoulddisplaymetainfo, theme]);

  //   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  //   const downloadDocumentAction = React.useCallback((): ReactElement => {
  //     const handleClick = (event: React.MouseEvent<HTMLElement>) => {
  //       setAnchorEl(event.currentTarget);
  //     };

  //     const handleClose = () => {
  //       setAnchorEl(null);
  //     };

  //     const handleMenuItemClick = (option: string) => {
  //       if (option === 'Download As PDF') {
  //         downloadFileAsPDF(selectedDocVersion);
  //       } else {
  //         isMultiPage
  //           ? downloadAllPages(
  //               isMultiPage,
  //               docDownloadData,
  //               fileExtensions,
  //               cardValues?.[0]?.name || 'download'
  //             )
  //           : downloadFile(
  //               docDownloadData,
  //               fileExtensions,
  //               cardValues?.[0]?.name || 'download'
  //             );
  //       }
  //       handleClose();
  //     };

  //     const options = ['Download Original', 'Download As PDF'];

  //     return (
  //       <Stack direction="row" spacing={2} sx={{pr: 3}}>
  //         <ButtonGroup variant="contained">
  //           <LoadingButton
  //             loading={isDownloading}
  //             sx={{
  //               bgcolor: theme.palette.primary.dark,
  //               color: 'white',
  //               borderTopLeftRadius: 'inherit',
  //               borderBottomLeftRadius: 'inherit',
  //               '&:hover': {bgcolor: theme.palette.primary.main},
  //               paddingLeft: '16px'
  //             }}
  //             onClick={() =>
  //               isMultiPage
  //                 ? downloadAllPages(
  //                     isMultiPage,
  //                     docDownloadData,
  //                     fileExtensions,
  //                     cardValues?.[0]?.name || 'download'
  //                   )
  //                 : downloadFile(
  //                     docDownloadData,
  //                     fileExtensions,
  //                     cardValues?.[0]?.name || 'download'
  //                   )
  //             }>
  //             Download
  //           </LoadingButton>
  //           <Button
  //             disabled={isDownloading}
  //             size="small"
  //             sx={{
  //               bgcolor: theme.palette.primary.dark,
  //               color: 'white',
  //               borderTopRightRadius: 'inherit !important',
  //               borderBottomRightRadius: 'inherit !important',
  //               height: 'auto !important',
  //               minWidth: 'auto',
  //               '&:hover': {bgcolor: theme.palette.primary.main}
  //             }}
  //             aria-controls={anchorEl ? 'split-button-menu' : undefined}
  //             // eslint-disable-next-line no-extra-boolean-cast
  //             aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
  //             aria-haspopup="menu"
  //             onClick={handleClick}>
  //             <ArrowDropDownIcon />
  //           </Button>
  //           <Menu
  //             id="split-button-menu"
  //             anchorEl={anchorEl}
  //             open={Boolean(anchorEl)}
  //             onClose={handleClose}>
  //             {options.map(option => (
  //               <MenuItem
  //                 key={option}
  //                 onClick={() => handleMenuItemClick(option)}>
  //                 {option}
  //               </MenuItem>
  //             ))}
  //           </Menu>
  //         </ButtonGroup>
  //       </Stack>
  //     );
  //   }, [
  //     isDownloading,
  //     selectedDocVersion,
  //     anchorEl,
  //     docDownloadData,
  //     fileExtensions,
  //     cardValues,
  //     isMultiPage
  //   ]);

  useEffect(() => {
    const docsList: any = [];
    const docsListTemp: any = [];
    bufferData.forEach((eachBuffer: any, index: number) => {
      if (!unSupportedFormats.includes(fileExtensions[index])) {
        docsList.push({
          uri: `${getBase64Flag(fileExtensions[index])}${Buffer.from(
            eachBuffer.data
          )?.toString('base64')}`,
          index: index
        });
        docsListTemp.push(index);
      }
    });
    setActiveDocument(docsList[0]);
  }, [bufferData, fileExtensions]);

  useEffect(() => {
    getPreviewer;
  }, [docs, activeDocument]);

  const handleChangeDocVersion = (version: string) => {
    setSelectedDocVersion(version);
    const tempObj: any = docVersionsData?.filter(
      (item: any) => item?.Version == version
    )[0];
    setDocData({...docData, ...tempObj});
    getDocumentsData(version, tempObj?.VersionID, {...docData, ...tempObj});
  };

  const renderSelectFields = (): ReactElement => {
    return (
      <SelectInput
        enableDefaultOption
        options={docVersionsData || []}
        placeholder={'Select Version'}
        name={'doc_versions_dropdown'}
        value={selectedDocVersion}
        onChange={(e: any) => handleChangeDocVersion(e.target.value)}
        optionValueKey={'Version'}
        optionLabelKey={'VersionLabel'}
        selectProps={{
          size: 'small'
        }}
      />
    );
  };
  const Controls: React.FC<ControlsProps> = ({
    zoomIn,
    zoomOut,
    resetTransform
  }) => {
    return (
      <ZoomButtonsDiv className="tools">
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.ZOOM_IN', {
              ns: 'private'
            })}
            onClick={zoomIn}>
            <ZoomIn
              sx={{
                color: theme.palette.common.white
              }}
            />
          </ZoomButton>
        )}
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.ZOOM_OUT', {
              ns: 'private'
            })}
            onClick={zoomOut}>
            <ZoomOut
              sx={{
                color: theme.palette.common.white
              }}
            />
          </ZoomButton>
        )}
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.RESET', {
              ns: 'private'
            })}
            onClick={resetTransform}>
            <Refresh
              sx={{
                color: theme.palette.common.white
              }}
            />
          </ZoomButton>
        )}
        {(!fileExtensions[0]?.toLowerCase().includes('txt') &&
          !textSelectMode &&
          ZoomInShowTextRef.current > 0) ||
        ZoomInShowTextRef.current < 0 ? (
          <PanToMoveDiv>
            {t('PRIVATE.DOCUMENT.MOVE_DOCUMENT', {
              ns: 'private'
            })}
          </PanToMoveDiv>
        ) : null}
        {['pdf', 'rtf'].includes(fileExtensions[0]?.toLowerCase()) && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.RESET', {
              ns: 'private'
            })}
            onClick={() => {
              setTextSelectMode(prev => !prev);
            }}>
            {textSelectMode ? (
              <DocumentScanner
                sx={{
                  color: theme.palette.common.white
                }}
              />
            ) : (
              <Title
                sx={{
                  color: theme.palette.common.white
                }}
              />
            )}
          </ZoomButton>
        )}
      </ZoomButtonsDiv>
    );
  };
  const renderButtons = React.useCallback((): ReactElement => {
    return (
      <AdditionalFeaturesButtonDiv>
        {/* {customDocViewerActions.map((item: any) => {
          if (item.action_name === FormConstants.DOCUMENTS_ACTION_TYPES.VIEW) {
            return viewDocumentAction();
          } else if (
            item.action_name ===
              FormConstants.DOCUMENTS_ACTION_TYPES.DOWNLOAD &&
            docDownloadData?.length > 0
          ) {
            // return downloadDocumentAction();
          } else {
            return null;
          }
        })} */}
        <Controls
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          resetTransform={resetTransform}
        />
      </AdditionalFeaturesButtonDiv>
    );
  }, [
    // customDocViewerActions,
    // docDownloadData,
    zoomIn,
    zoomOut,
    resetTransform
  ]);

  return isLoading ? (
    <DocViewContainer isMultiPage={fileExtensions?.length > 1} height={height}>
      <ComponentLoader loaderSize={10} />
    </DocViewContainer>
  ) : (
    <Stack
      direction={'column'}
      sx={{
        position: 'absolute',
        width: '-webkit-fill-available',
        height: '100%',
        py: 2
      }}>
      {iframeSrc ? (
        <Box
          sx={{
            position: 'relative',
            top: '0',
            width: '-webkit-fill-available',
            height: '-webkit-fill-available'
          }}>
          {iframeSrc.MimeType === 'linkcode' ? (
            <a
              href={iframeSrc.UploadedFile}
              target="_blank"
              rel="noopener noreferrer"
              title="Open YouTube video"
              style={{
                display: 'flex',
                width: '100%',
                height: '72vh',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none'
              }}>
              Watch Video
            </a>
          ) : (
            (() => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(
                iframeSrc?.UploadedFile,
                'text/html'
              );

              // Query the iframe element to get its src and title attributes
              const iframeSrcName =
                doc.querySelector('iframe')?.getAttribute('src') ?? '';
              const titlename =
                doc.querySelector('iframe')?.getAttribute('title') ?? '';

              return (
                <iframe
                  src={iframeSrcName}
                  title={titlename}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    padding: '16px'
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                />
              );
            })()
          )}
        </Box>
      ) : (
        <>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                paddingBottom: 1,
                borderBottom: '1px dotted',
                borderColor: 'gray'
              }}>
              <Box>{renderButtons()}</Box>
              <Box
                sx={{
                  px: 3
                }}>
                {docVersionsData?.length ? renderSelectFields() : null}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              position: 'relative',
              top: '0',
              width: '100%',
              overflowY: 'scroll',
              justifyContent: 'center'
            }}>
            <DocViewContainer
              isMultiPage={fileExtensions?.length > 1}
              height={height}>
              {getPreviewer}
            </DocViewContainer>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default CustomLMSDocViewer;
